// File: src/ErrorBoundary.js
// OOOSH Driver Verification - React Error Boundary
// Catches unhandled rendering errors that would normally show a white screen
// Reports as CRITICAL severity (triggers email alert)
// Shows friendly error page with contact info and retry option
//
// Usage in App.js:
//   import ErrorBoundary from './ErrorBoundary';
//   <ErrorBoundary><DriverVerificationApp /></ErrorBoundary>

import React from 'react';
import reportError from './utils/errorReporter';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Report to our logging system as CRITICAL — this means the app crashed
    reportError({
      page: 'ErrorBoundary (App Crash)',
      error: error.message || 'Unknown rendering error',
      severity: 'critical',
      additionalInfo: errorInfo?.componentStack 
        ? errorInfo.componentStack.substring(0, 500)  // Trim stack to avoid huge payloads
        : null
    });
  }

  handleRefresh = () => {
    // Reload the page — smart routing will put them in the right place
    window.location.reload();
  };

  handleStartOver = () => {
    // Clear session and go to the base URL with just the job parameter
    const urlParams = new URLSearchParams(window.location.search);
    const jobParam = urlParams.get('job');
    sessionStorage.clear();
    
    if (jobParam) {
      window.location.href = `/?job=${jobParam}`;
    } else {
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          fontFamily: "'Montserrat', sans-serif"
        }}>
          <div style={{
            maxWidth: '28rem',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            padding: '2rem',
            textAlign: 'center'
          }}>
            {/* Error icon */}
            <div style={{
              width: '4rem',
              height: '4rem',
              backgroundColor: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>

            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#111827',
              marginBottom: '0.75rem'
            }}>
              Something went wrong
            </h2>
            
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              marginBottom: '1.5rem',
              lineHeight: 1.5
            }}>
              We've been notified and are looking into it. 
              Your progress has been saved — please try refreshing.
            </p>

            {/* Action buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                onClick={this.handleRefresh}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Refresh page
              </button>
              
              <button
                onClick={this.handleStartOver}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Start again
              </button>
            </div>

            {/* Contact info */}
            <div style={{
              marginTop: '1.5rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid #e5e7eb'
            }}>
              <p style={{
                fontSize: '0.8125rem',
                color: '#6b7280',
                marginBottom: '0.5rem'
              }}>
                Still having trouble? Get in touch:
              </p>
              <p style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}>
                📞 <a href="tel:01273911382" style={{ color: '#7c3aed', fontWeight: 500, textDecoration: 'none' }}>01273 911382</a>
              </p>
              <p style={{ fontSize: '0.875rem', margin: '0.25rem 0' }}>
                📧 <a href="mailto:info@oooshtours.co.uk" style={{ color: '#7c3aed', fontWeight: 500, textDecoration: 'none' }}>info@oooshtours.co.uk</a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;  